import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import './MapComponent.css';

// Default map center (can be adjusted as needed)
const defaultCenter = {
  lat: 20.5937, // Central India coordinates
  lng: 78.9629
};

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
};

const MapComponent = ({ boats, selectedBoat, boatRoute }) => {
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [center, setCenter] = useState(defaultCenter);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyA6wWF47cejmrwic65drmPiOIhrawrYnzw', // TODO: Add your Google Maps API key
    // Consider adding a placeholder key or instructions for the user to add their own
  });

  const onLoad = useCallback(map => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Center map on selected boat
  useEffect(() => {
    if (map && selectedBoat) {
      const boat = boats.find(b => {
        // Handle both registry boats and location-only boats
        return b.boatId === selectedBoat || 
               (b.lastLocation && b.lastLocation.boatId === selectedBoat);
      });
      
      if (boat && boat.lastLocation) {
        const { latitude, longitude } = boat.lastLocation;
        setCenter({ lat: latitude, lng: longitude });
        map.panTo({ lat: latitude, lng: longitude });
      }
    }
  }, [map, selectedBoat, boats]);

  // Fit bounds to show the entire route when a route is available
  useEffect(() => {
    if (map && boatRoute && boatRoute.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      boatRoute.forEach(point => {
        bounds.extend({ lat: point.latitude, lng: point.longitude });
      });
      map.fitBounds(bounds);
    }
  }, [map, boatRoute]);

  if (loadError) {
    return <div className="map-error">Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div className="map-loading">Loading maps...</div>;
  }

  // Function to get valid boatId from a boat object
  const getBoatId = (boat) => {
    return boat.boatId || (boat.lastLocation && boat.lastLocation.boatId);
  };

  // Filter boats that have a valid lastLocation
  const validBoats = boats.filter(boat => boat && boat.lastLocation);

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={10}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={mapOptions}
    >
      {/* Render all boat markers */}
      {validBoats.map(boat => {
        // Skip boats without location data
        if (!boat.lastLocation) return null;
        
        const position = {
          lat: boat.lastLocation.latitude,
          lng: boat.lastLocation.longitude
        };
        
        const boatId = getBoatId(boat);
        
        return (
          <Marker
            key={boatId}
            position={position}
            icon={{
              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 6,
              fillColor: selectedBoat === boatId ? '#1E88E5' : '#757575',
              fillOpacity: 1,
              strokeColor: '#FFF',
              strokeWeight: 1,
              rotation: boat.lastLocation.heading || 0
            }}
            onClick={() => setSelectedMarker(boat)}
          />
        );
      })}
      
      {/* Info window for selected marker */}
      {selectedMarker && selectedMarker.lastLocation && (
        <InfoWindow
          position={{
            lat: selectedMarker.lastLocation.latitude,
            lng: selectedMarker.lastLocation.longitude
          }}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <div className="info-window">
            <h3>{selectedMarker.displayName || getBoatId(selectedMarker)}</h3>
            <p>
              <strong>Speed:</strong> {selectedMarker.lastLocation.speed} km/h<br />
              <strong>Heading:</strong> {selectedMarker.lastLocation.heading}°<br />
              <strong>Last update:</strong> {new Date(selectedMarker.lastLocation.timestamp).toLocaleString()}
            </p>
          </div>
        </InfoWindow>
      )}
      
      {/* Route polyline for selected boat */}
      {boatRoute && boatRoute.length > 0 && (
        <Polyline
          path={boatRoute.map(point => ({
            lat: point.latitude,
            lng: point.longitude
          }))}
          options={{
            strokeColor: '#1E88E5',
            strokeOpacity: 0.8,
            strokeWeight: 4,
          }}
        />
      )}
    </GoogleMap>
  );
};

export default MapComponent; 